import { Box, Typography } from '@material-ui/core';
import { KeyboardArrowRight } from '@material-ui/icons';
import React, { useEffect, useMemo, useState } from 'react';
import { LinearProgressOverlay } from '@campfire/linear-progress-overlay';
import { SearchField } from '../../../common/inputs/SearchField';
import { Avatar } from '../../general/user-profile/components/Avatar';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';
import { useCampfireFetch } from '../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../global/config/useApiUrl';
import { VOLABY_REGION } from '../create-trial-account/CreateTrialAccountConsts';

interface OrgList {
  id: string;
  name: string;
  fqdn: string;
  logo: string | null;
  region: string;
}

const mapRegionName = (code: string) =>
  VOLABY_REGION.find((region) => region.value.toLowerCase() === code.toLowerCase())?.label ?? '';

export const TrialFindOrgScreen = () => {
  const [searchValue, setSearchValue] = useState<string>('');
  const { theme, isSm, isMobile } = useCampfireTheme();
  const [orgsData, setOrgsData] = useState<Array<OrgList>>([]);
  const apiUrl = useApiUrl();
  const campfireFetch = useCampfireFetch<{ publicTenants: Array<OrgList> }>({ defer: true, withAuth: false });

  useEffect(() => {
    campfireFetch
      .run({
        url: `${apiUrl}/tm/tenant/public-tenants`,
        method: 'get',
      })
      .then((response) => setOrgsData(response?.data?.data?.publicTenants ?? []));
  }, []);

  const results = useMemo(
    () => orgsData.filter((org) => (!searchValue ? false : org.name.toLowerCase().includes(searchValue.toLowerCase()))),
    [orgsData, searchValue]
  );
  const loading = campfireFetch.isLoading;

  return (
    <Box display='flex' flexDirection='column' mx={isMobile ? '24px' : '34px'} my='28px'>
      <LinearProgressOverlay color='primary' isLoading={loading} />
      <Typography variant='h3' style={{ fontWeight: 700, fontSize: '24px', letterSpacing: '-2%', color: '#343434' }}>
        Find Your Organisation
      </Typography>
      <Typography style={{ color: theme.color.grey.neutralBrand400 }}>
        {`Enter your organisation's name below and we'll try and connect you to them.`}
      </Typography>
      <Box width={isSm ? '100%' : '60%'} alignSelf='center' py={2}>
        <Box py={2}>
          <SearchField
            fullWidth
            value={searchValue}
            placeholder='search for your organisation'
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchValue(e.target.value)}
          />
        </Box>
        {results.length > 0 ? (
          <>
            <Typography
              style={{
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '-2%',
                borderBottom: '1px solid #00000040',
                marginTop: '8px',
                paddingBottom: '8px',
              }}
              color='textPrimary'
            >
              Results
            </Typography>
            {results.map((result) => (
              <a href={`//${result.fqdn}/sign-in`} style={{ textDecoration: 'none', color: 'unset' }}>
                <Box display='flex' flex={1} alignItems='center' borderBottom='1px solid #00000040'>
                  <Avatar size={52} avatarUrl={result?.logo ?? ''} style={{ marginTop: '8px', marginBottom: '8px' }} />
                  <Box px='14px' flex={2}>
                    <Typography style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '-2%', color: '#000000' }}>
                      {result.name}
                    </Typography>
                    <Typography color='textPrimary' style={{ fontSize: '12px', fontWeight: 400, letterSpacing: '-2%' }}>
                      {mapRegionName(result.region)}
                    </Typography>
                  </Box>
                  <KeyboardArrowRight style={{ justifySelf: 'right', color: theme.color.grey.neutralBrand400 }} />
                </Box>
              </a>
            ))}
          </>
        ) : (
          searchValue &&
          !loading && (
            <Typography
              color='textSecondary'
              style={{ fontWeight: 400 }}
            >{`We couldn’t find any results for your search.
          You’ll need to contact the organisation directly for their Volaby details`}</Typography>
          )
        )}
      </Box>
    </Box>
  );
};
